import { useState } from "react";
import { useLocation, useNavigate, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, ArrowUpRight, Lock } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { ADMIN } from "@/constants/testIds";

const EASE = [0.76, 0, 0.24, 1];

const formatDetail = (d) => {
  if (!d) return "Something went wrong. Please try again.";
  if (typeof d === "string") return d;
  if (Array.isArray(d))
    return d.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" · ");
  return String(d);
};

export default function AdminLoginPage() {
  const { user, signIn } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(location.state?.error || "");

  if (user) {
    const from = location.state?.from || "/admin";
    return <Navigate to={from} replace />;
  }

  const onGoogleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await signIn(email, password);
      toast.success("Signed in.");
      navigate(location.state?.from || "/admin", { replace: true });
    } catch (err) {
      const msg = formatDetail(err?.response?.data?.detail) || err?.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-32 pb-16 bg-[var(--bg)] flex items-start justify-center">
      <div className="max-w-[1440px] w-full mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-12">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, ease: EASE }}
          className="md:col-span-6"
        >
          <p className="overline mb-6">Admin · Sign in</p>
          <h1 className="font-display text-5xl md:text-7xl tracking-[-0.03em] leading-[0.92] text-[var(--ink)]">
            The{" "}
            <span className="font-serif-accent italic text-[var(--accent)]">
              inbox
            </span>{" "}
            behind the practice.
          </h1>
          <p className="mt-8 text-[var(--ink-80)] max-w-md leading-relaxed">
            Enquiries from the Orphion site land here. Sign in with your
            admin credentials to view, triage and respond.
          </p>
          <div className="mt-14 border-t border-[var(--line-strong)] pt-8 flex items-center gap-3 text-[var(--ink-50)]">
            <Lock className="w-4 h-4" />
            <span className="text-xs uppercase tracking-[0.24em]">
              Encrypted · Single-admin
            </span>
          </div>
        </motion.div>

        <motion.form
          onSubmit={onSubmit}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: EASE, delay: 0.15 }}
          data-testid={ADMIN.loginForm}
          className="md:col-span-6 md:col-start-8 space-y-2"
        >
          <div>
            <label className="overline block mb-2">Email</label>
            <input
              required
              type="email"
              className="editorial-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@orphionglobalhr.com"
              autoComplete="email"
              data-testid={ADMIN.loginEmail}
            />
          </div>
          <div className="pt-4">
            <label className="overline block mb-2">Password</label>
            <input
              required
              type="password"
              className="editorial-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              autoComplete="current-password"
              data-testid={ADMIN.loginPassword}
            />
          </div>

          {error && (
            <div className="text-red-600 text-sm pt-4" data-testid={ADMIN.loginError}>
              {error}
            </div>
          )}

          <div className="pt-10">
            <button
              type="submit"
              disabled={loading}
              className="btn-pill disabled:opacity-60"
              data-testid={ADMIN.loginSubmit}
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Signing in…</span>
                </>
              ) : (
                <>
                  <span>Sign in</span>
                  <ArrowUpRight className="w-4 h-4" />
                </>
              )}
            </button>
          </div>

          <div className="pt-8 flex items-center gap-4">
            <span className="h-px flex-1 bg-[var(--line)]" />
            <span className="text-[10px] uppercase tracking-[0.28em] text-[var(--ink-50)]">
              or
            </span>
            <span className="h-px flex-1 bg-[var(--line)]" />
          </div>

          <div className="pt-8">
            <button
              type="button"
              onClick={onGoogleSignIn}
              className="btn-ghost"
              data-testid="admin-google-signin-btn"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" aria-hidden="true">
                <path
                  fill="#4285F4"
                  d="M23.49 12.27c0-.79-.07-1.54-.19-2.27H12v4.51h6.47c-.29 1.48-1.14 2.73-2.4 3.58v3h3.86c2.26-2.09 3.56-5.17 3.56-8.82z"
                />
                <path
                  fill="#34A853"
                  d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.86-3c-1.08.72-2.45 1.16-4.07 1.16-3.13 0-5.78-2.11-6.73-4.96H1.29v3.09C3.26 21.3 7.31 24 12 24z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.27 14.29c-.25-.72-.38-1.49-.38-2.29s.14-1.57.38-2.29V6.62H1.29C.47 8.24 0 10.06 0 12s.47 3.76 1.29 5.38l3.98-3.09z"
                />
                <path
                  fill="#EA4335"
                  d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.31 0 3.26 2.7 1.29 6.62l3.98 3.09C6.22 6.86 8.87 4.75 12 4.75z"
                />
              </svg>
              <span>Continue with Google</span>
            </button>
            <p className="mt-4 text-xs text-[var(--ink-50)] leading-relaxed max-w-xs">
              Google sign-in is enabled for the authorised admin account only.
            </p>
          </div>
        </motion.form>
      </div>
    </div>
  );
}
